import { useContext, useState } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';
import TagChip from '../shared/TagChip';
import { getPriorityLabel, getPriorityStyle, getStatusStyle } from '../shared/helpers/statusBadge';
import InternalAdminGrievanceService, { InternalGrievanceCard } from '../services/InternalAdminGrievanceService';
import { getDomainValueForCode, isGrievanceCategoryPushedOnly } from '../../../../library/Core/SmartFunctions';
import { getStudentImage } from '../../../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../../../library/SimpleControls/InitialsAvatar';

// AC5: Warm tint on Pending Review rows (status === 'new')
const PENDING_REVIEW_TINT = '#fffbeb';

// ── Institute Push cell — 3 mutually exclusive states ──────────────────────
const InstitutePushCell = ({
    grievance,
    onPush,
    onEdit,
    onUndo,
}: {
    grievance: InternalGrievanceCard;
    onPush: () => void;
    onEdit: () => void;
    onUndo: () => void;
}) => {
    const { push, status } = grievance;
    const isClosed = status === 'resolved' || status === 'discarded';

    // Already pushed — show Pushed badge + priority; hide Undo/Edit when closed
    if (push) {
        const ipStyle = getPriorityStyle(push.institutePriority ?? grievance.priority);

        return (
            <div>
                <div className="d-flex flex-wrap gap-1 mb-1">
                    <span
                        className="badge rounded-pill font-13 font-500 px-2 py-1 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}
                    >
                        <i className="bi bi-check-all font-16 position-relative me-1"></i> Pushed
                    </span>
                    <span
                        className="badge rounded-pill font-13 font-500 px-2 py-1 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: ipStyle.bg, color: ipStyle.text }}
                    >
                        {getPriorityLabel(push.institutePriority ?? grievance.priority)}
                    </span>
                </div>
                {!isClosed && (
                    <div className="d-flex gap-1 mt-1">
                        <button
                            className="btn btn-sm btn-outline-secondary font-12 px-2 py-0"
                            onClick={onUndo}
                        >
                            Undo
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary font-12 px-2 py-0"
                            onClick={onEdit}
                        >
                            Edit
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Resolved / discarded and not pushed — no action available
    if (isClosed) return null;

    // Eligible unpushed — Push to Institute button
    return (
        <button
            className="btn-darkblue text-white h-auto rounded-pill px-2 py-1 font-13 font-500"
            onClick={onPush}
        >
            <span>Push to Institute</span>
        </button>
    );
};

// ── Main Results component ──────────────────────────────────────────────────
const Results = () => {
    const { state, dispatch } = useContext(SmartContext);
    const grievances: InternalGrievanceCard[] = state.data?.grievanceList ?? [];

    // AC14: Undo confirmation state
    const [undoPendingId, setUndoPendingId] = useState<number | null>(null);

    const handleUndoConfirm = async () => {
        if (undoPendingId === null) return;
        try {
            await InternalAdminGrievanceService.undoPush(undoPendingId);
            const activeTab = state.data?.searchCriteria?.tab ?? 'total';
            // On the "Pushed to Institute" tab the row no longer belongs — remove it.
            // On other tabs keep the row but clear its push record.
            const updated = activeTab === 'pushedToInstitute'
                ? grievances.filter((g) => g.id !== undoPendingId)
                : grievances.map((g) => g.id === undoPendingId ? { ...g, push: null } : g);
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: updated, errorMessages: [] } });
            const prev = state.data?.tabCounts ?? {};
            dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: {
                    dataKey: 'tabCounts',
                    value: { ...prev, pushedToInstitute: Math.max(0, (prev.pushedToInstitute ?? 1) - 1) },
                    errorMessages: [],
                },
            });
        } catch {
            // no-op: leave state unchanged on error
        } finally {
            setUndoPendingId(null);
        }
    };

    const handlePush = (grievance: InternalGrievanceCard) => {
        window.dispatchEvent(new CustomEvent('openPushToInstitute', { detail: { mode: 'create', grievance } }));
    };

    const handleEdit = (grievance: InternalGrievanceCard) => {
        window.dispatchEvent(new CustomEvent('openPushToInstitute', { detail: { mode: 'edit', grievance } }));
    };

    const handleViewDetails = (grievance: InternalGrievanceCard) => {
        window.dispatchEvent(new CustomEvent('openGrievanceDetails', { detail: { id: grievance.id } }));
    };

    if (grievances.length === 0) {
        return (
            <div className="text-center py-5">
                <img
                    src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/no-result-img-new.svg"
                    width="220"
                    className="mb-3"
                    alt="No grievances"
                />
                <p className="font-18 text-muted">No grievances match the current filters.</p>
            </div>
        );
    }

    return (
        <>
            <div className="table-responsive attendee_dtl">
                <table className="table table-hover align-middle mb-0" style={{ minWidth: '1200px' }}>
                    <thead>
                        <tr className="text-uppercase text-secondary small">
                            <th className="font-500 py-3">Grievance ID</th>
                            <th className="font-500 py-3">Alumni Details</th>
                            <th className="font-500 py-3">Program / Batch</th>
                            <th className="font-500 py-3">Type &amp; Visibility</th>
                            <th className="font-500 py-3">Priority</th>
                            <th className="font-500 py-3">Status</th>
                            <th className="font-500 py-3">Institute Push</th>
                            <th className="font-500 py-3 text-end pe-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {grievances.map((g) => {
                            const isPendingReview = g.status === 'new';
                            const priorityStyle   = getPriorityStyle(g.priority);
                            const statusStyle     = getStatusStyle(g.status);
                            const statusLabel     = g.status === 'new' ? 'New' : statusStyle.label;
                            const hasTags         = g.push?.tags && g.push.tags.length > 0;

                            return (
                                <tr
                                    key={g.id}
                                    className={isPendingReview ? 'highlight' : ''}
                                    style={isPendingReview ? { backgroundColor: PENDING_REVIEW_TINT } : undefined}
                                >
                                    {/* Col 1: Grievance ID + received date */}
                                    <td>
                                        <button
                                            className="btn btn-link p-0 font-14 font-500 text-dark-blue text-decoration-none d-block text-start"
                                            onClick={() => handleViewDetails(g)}
                                        >
                                            {g.code}
                                        </button>
                                        <div className="text-muted font-11">{g.receivedDate}</div>
                                    </td>

                                    {/* Col 2: Alumni Details */}
                                    <td style={{ minWidth: '200px' }}>
                                        <div className="d-flex align-items-start gap-2">
                                            {/* Avatar: links to profile when uuid available */}
                                            <a
                                                href={g.alumni.uuid ? `/alumni/${g.alumni.uuid}/profile` : '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => { if (!g.alumni.uuid) e.preventDefault(); }}
                                            >
                                                {hasProfilePicture(g.alumni.avatarUrl) ? (
                                                    <img
                                                        src={getStudentImage({ profilePictureFilePath: g.alumni.avatarUrl, genderCode: g.alumni.genderCode })}
                                                        alt={g.alumni.name}
                                                        className="rounded-circle flex-shrink-0 ratio-40"
                                                        style={{ width: 40, height: 40, objectFit: 'cover', cursor: g.alumni.uuid ? 'pointer' : 'default' }}
                                                    />
                                                ) : (
                                                    <InitialsAvatar name={g.alumni.name} size={40} />
                                                )}
                                            </a>
                                            <div>
                                                <a
                                                    href={g.alumni.uuid ? `/alumni/${g.alumni.uuid}/profile` : '#'}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-15 font-500 text-dark-blue lh-1 mb-1 text-decoration-none d-block"
                                                    onClick={(e) => { if (!g.alumni.uuid) e.preventDefault(); }}
                                                >
                                                    {g.alumni.name}
                                                </a>
                                                {g.alumni.tier && (
                                                    <div className="font-14 text-secondary mb-1">
                                                        <i className="bi bi-star-fill text-warning me-1"></i>
                                                        {g.alumni.tier}
                                                    </div>
                                                )}
                                                {/* Importance tag chips */}
                                                {hasTags && (
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {g.push!.tags.slice(0, 3).map((tag) => (
                                                            <TagChip key={tag.tagCode} tag={tag} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Col 3: Program / Batch */}
                                    <td>
                                        <div className="font-14 font-500 text-dark">{g.alumni.program || '—'}</div>
                                        {g.alumni.batch && (
                                            <div className="font-13 text-secondary">Batch {g.alumni.batch}</div>
                                        )}
                                    </td>

                                    {/* Col 4: Type & Visibility */}
                                    <td style={{ minWidth: '160px' }}>
                                        <div className="font-14 font-500 text-dark mb-1">
                                            {getDomainValueForCode(g.category, 'GRIEVANCE_CATEGORY', state)}
                                        </div>
                                        {g.isInternalOnly || isGrievanceCategoryPushedOnly(g.category, state) ? (
                                            <span className="badge rounded-pill bg-secondary bg-opacity-10 border border-secondary-subtle text-secondary-emphasis font-13 font-500 px-2 py-1">
                                                <span className="text-secondary">●</span> Internal Only
                                            </span>
                                        ) : (
                                            <span className="badge rounded-pill bg-primary bg-opacity-10 border border-primary-subtle text-primary-emphasis font-13 font-500 px-2 py-1">
                                                <span className="text-success">●</span> Always to Institute
                                            </span>
                                        )}
                                    </td>

                                    {/* Col 5: Priority */}
                                    <td>
                                        <span
                                            className="badge rounded-pill font-13 font-500 px-3 py-2"
                                            style={{ backgroundColor: priorityStyle.bg, color: priorityStyle.text }}
                                        >
                                            {getPriorityLabel(g.priority)}
                                        </span>
                                    </td>

                                    {/* Col 6: Status */}
                                    <td>
                                        <span
                                            className="badge rounded-pill font-13 font-500 px-3 py-2"
                                            style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                                        >
                                            {statusLabel}
                                        </span>
                                    </td>

                                    {/* Col 7: Institute Push */}
                                    <td style={{ minWidth: '140px' }}>
                                        <InstitutePushCell
                                            grievance={g}
                                            onPush={() => handlePush(g)}
                                            onEdit={() => handleEdit(g)}
                                            onUndo={() => setUndoPendingId(g.id)}
                                        />
                                    </td>

                                    {/* Col 8: Action */}
                                    <td className="text-end pe-3">
                                        <button
                                            className="btn btn-link p-0 font-13 text-secondary text-decoration-none"
                                            onClick={() => handleViewDetails(g)}
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* AC14: Undo confirmation dialog */}
            {undoPendingId !== null && (
                <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title font-16 font-600">Undo Push to Institute</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setUndoPendingId(null)}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Are you sure? The institute will lose access to this grievance and it will be removed from their dashboard.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setUndoPendingId(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleUndoConfirm}
                                >
                                    Yes, Undo Push
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Results;
